import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { API } from "@/lib/apiBase";

export default function CreditWineDialog({ wineId, wineName }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(90);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await axios.post(`${API}/wines/${wineId}/credit`, { rating: Number(rating), notes });
      toast.success("Credit submitted for admin approval");
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="credit-wine-btn" className="bg-merlot hover:bg-merlot-light text-white gap-2">
          <Award className="w-4 h-4" /> Credit this Wine
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wine-paper border-white/10 max-w-md" data-testid="credit-wine-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white/90">Credit Wine</DialogTitle>
          <DialogDescription className="text-white/40 font-body text-sm">
            Submit your rating for "{wineName}". An admin will review and apply it to the wine.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Rating (0-100)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              required
              data-testid="credit-rating"
              className="bg-transparent border-white/10 text-white/90 font-body h-10"
            />
          </div>
          <div>
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="credit-notes"
              placeholder="Tasting impressions, reasoning for the score..."
              className="bg-transparent border-white/10 text-white/90 font-body"
            />
          </div>
          {error && <p className="text-sm text-red-400 font-body" data-testid="credit-error">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/70" data-testid="credit-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-merlot hover:bg-merlot-light text-white" data-testid="credit-submit">
              {saving ? "Submitting..." : "Submit Credit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
